import { API, axios } from './api';

// Accesso rapido con passkey (Face ID / impronta). Le rotte sono in server.py (/api/auth/passkey/...).
// Le opzioni si chiedono al server PRIMA del tocco sul bottone: Safari accetta la richiesta di
// Face ID solo se parte subito dal tocco, senza una chiamata di rete in mezzo.

const CHIAVE_DISPOSITIVO = 'mk_passkey';     // { email, id }: su questo dispositivo c'è una passkey
const CHIAVE_NON_ORA = 'mk_passkey_non_ora'; // fino a quando non riproporre l'attivazione (ms)
const TRENTA_GIORNI = 30 * 24 * 60 * 60 * 1000;
const VALIDITA_OPZIONI = 4 * 60 * 1000;      // la sfida del server dura 5 minuti

const inBuffer = (b64url) => {
  const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(b64url.length / 4) * 4, '=');
  return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0)).buffer;
};

const inBase64url = (buffer) => btoa(String.fromCharCode(...new Uint8Array(buffer)))
  .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

const leggiJSON = (chiave) => {
  try { return JSON.parse(localStorage.getItem(chiave) || 'null'); } catch { return null; }
};

const passkeySupportate = async () => {
  try {
    return !!(window.PublicKeyCredential
      && await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable());
  } catch {
    return false;
  }
};

// Nome del sistema di sblocco e del dispositivo, per i testi e per l'elenco nel profilo
const piattaforma = () => {
  const ua = navigator.userAgent || '';
  const p = navigator.userAgentData?.platform || navigator.platform || '';
  const browser = /Edg\//.test(ua) ? 'Edge' : /CriOS|Chrome\//.test(ua) ? 'Chrome'
    : /FxiOS|Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'browser';
  const conBrowser = (d) => `${d} · ${browser}`;
  const ipad = /iPad/.test(ua) || (/Mac/.test(p) && navigator.maxTouchPoints > 1);
  if (/iPhone|iPod/.test(ua) || ipad) {
    return { con: 'con Face ID', sistema: 'Face ID', icona: 'volto', dispositivo: conBrowser(ipad ? 'iPad' : 'iPhone') };
  }
  if (/Android/.test(ua)) return { con: "con l'impronta", sistema: 'impronta', icona: 'impronta', dispositivo: conBrowser('Android') };
  if (/Mac/.test(p) || /Macintosh/.test(ua)) return { con: 'con Touch ID', sistema: 'Touch ID', icona: 'impronta', dispositivo: conBrowser('Mac') };
  if (/Win/.test(p) || /Windows/.test(ua)) return { con: 'con Windows Hello', sistema: 'Windows Hello', icona: 'volto', dispositivo: conBrowser('PC Windows') };
  return { con: 'con Face ID / impronta', sistema: 'Face ID / impronta', icona: 'impronta', dispositivo: conBrowser('Dispositivo') };
};

// ---- segni lasciati su questo dispositivo
const dispositivoConPasskey = () => leggiJSON(CHIAVE_DISPOSITIVO);
const segnaDispositivo = (dati) => localStorage.setItem(CHIAVE_DISPOSITIVO, JSON.stringify(dati));
const dimenticaDispositivo = () => localStorage.removeItem(CHIAVE_DISPOSITIVO);
const rimandaProposta = () => localStorage.setItem(CHIAVE_NON_ORA, String(Date.now() + TRENTA_GIORNI));
const propostaRimandata = () => Number(localStorage.getItem(CHIAVE_NON_ORA) || 0) > Date.now();

// ---- conversioni tra il JSON del server e gli oggetti del browser
const opzioniCreazione = (o) => ({
  ...o,
  challenge: inBuffer(o.challenge),
  user: { ...o.user, id: inBuffer(o.user.id) },
  excludeCredentials: (o.excludeCredentials || []).map((c) => ({ ...c, id: inBuffer(c.id) })),
});

const opzioniAccesso = (o) => ({
  ...o,
  challenge: inBuffer(o.challenge),
  allowCredentials: (o.allowCredentials || []).map((c) => ({ ...c, id: inBuffer(c.id) })),
});

const credenzialeJSON = (c) => {
  const r = c.response;
  const base = {
    id: c.id,
    rawId: inBase64url(c.rawId),
    type: c.type,
    clientExtensionResults: c.getClientExtensionResults ? c.getClientExtensionResults() : {},
    ...(c.authenticatorAttachment ? { authenticatorAttachment: c.authenticatorAttachment } : {}),
  };
  if (r.attestationObject) {
    return {
      ...base,
      response: {
        clientDataJSON: inBase64url(r.clientDataJSON),
        attestationObject: inBase64url(r.attestationObject),
        transports: r.getTransports ? r.getTransports() : [],
      },
    };
  }
  return {
    ...base,
    response: {
      clientDataJSON: inBase64url(r.clientDataJSON),
      authenticatorData: inBase64url(r.authenticatorData),
      signature: inBase64url(r.signature),
      ...(r.userHandle ? { userHandle: inBase64url(r.userHandle) } : {}),
    },
  };
};

const opzioniValide = (preparate) => preparate && Date.now() - preparate.preso < VALIDITA_OPZIONI;

// ---- registrazione (utente entrato)
const preparaRegistrazione = async () => {
  const { data } = await axios.post(`${API}/auth/passkey/registra/opzioni`);
  return { ...data, preso: Date.now() };
};

const registraPasskey = async (preparate, email) => {
  const pronte = opzioniValide(preparate) ? preparate : await preparaRegistrazione();
  const credenziale = await navigator.credentials.create({ publicKey: opzioniCreazione(pronte.opzioni) });
  const { data } = await axios.post(`${API}/auth/passkey/registra/verifica`, {
    sfida_id: pronte.sfida_id,
    credenziale: credenzialeJSON(credenziale),
    nome_dispositivo: piattaforma().dispositivo,
  });
  segnaDispositivo({ email: (email || '').toLowerCase(), id: data.id });
  return data;
};

// ---- accesso (senza login): restituisce la stessa risposta di /auth/login
// passkeyId: l'id della passkey di questo dispositivo; la risposta dice se è ancora attiva
const preparaAccesso = async (email, passkeyId) => {
  const { data } = await axios.post(`${API}/auth/passkey/accedi/opzioni`, { email, ...(passkeyId ? { passkey_id: passkeyId } : {}) });
  return { ...data, preso: Date.now() };
};

const accediConPasskey = async (preparate, email) => {
  const pronte = opzioniValide(preparate) ? preparate : await preparaAccesso(email);
  const credenziale = await navigator.credentials.get({ publicKey: opzioniAccesso(pronte.opzioni) });
  const { data } = await axios.post(`${API}/auth/passkey/accedi/verifica`, {
    sfida_id: pronte.sfida_id,
    credenziale: credenzialeJSON(credenziale),
  });
  aggiornaEmailDispositivo(data?.user_info?.email);
  return data;
};

// Questo dispositivo si ricorda l'email con cui è stata creata la passkey, e la manda per chiedere la sfida: se lo
// staff cambia l'email al cliente, quella ricordata invecchia e l'accesso rapido non trova più niente da firmare.
// Dopo ogni accesso riuscito (password, codice o passkey) l'email ricordata si rimette a posto da sé.
const aggiornaEmailDispositivo = (email) => {
  const dispositivo = dispositivoConPasskey();
  const pulita = (email || '').trim().toLowerCase();
  if (!dispositivo || !pulita || dispositivo.email === pulita) return;
  segnaDispositivo({ ...dispositivo, email: pulita });
};

// Annullato dall'utente (o nessuna passkey adatta sul dispositivo): nessun messaggio
const annullato = (err) => err?.name === 'NotAllowedError' || err?.name === 'AbortError';

export {
  passkeySupportate, piattaforma, dispositivoConPasskey, segnaDispositivo, dimenticaDispositivo,
  rimandaProposta, propostaRimandata, preparaRegistrazione, registraPasskey, preparaAccesso,
  accediConPasskey, aggiornaEmailDispositivo, annullato,
};
